/**
 * Create Canonical Cluster Modal
 *
 * Creates a cluster via itemRelationshipsStore.createGroup (canonical).
 * Opened from Item Relationship Inspector.
 */

import { useState, useMemo, useEffect, useRef } from 'react';
import { X, Search, CheckCircle, Circle, Layers, Star, AlertCircle, Plus } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { ClusterSubtype } from '../../../types/prioritization/itemRelationships';

interface CreateCanonicalClusterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (groupId: string) => void;
  /** The item this modal was opened from — pre-selected as first member. Optional when opened from relationship page. */
  sourceItemId?: string;
}

const CLUSTER_SUBTYPES: { value: ClusterSubtype; label: string; description: string }[] = [
  { value: 'duplicate', label: 'Duplicate', description: 'Same request submitted multiple times' },
  { value: 'variant', label: 'Variant', description: 'Similar requests with minor differences' },
];

export function CreateCanonicalClusterModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: CreateCanonicalClusterModalProps) {
  const userId = useUserStore((s) => s.userId);
  const createGroup = useItemRelationshipsStore((s) => s.createGroup);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const createItem = useItemsStore((s) => s.createItem);

  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(sourceItemId ? [sourceItemId] : []);
  const [canonicalItemId, setCanonicalItemId] = useState<string>(sourceItemId ?? '');
  const [clusterTitle, setClusterTitle] = useState('');
  const [subtype, setSubtype] = useState<ClusterSubtype>('duplicate');
  const [rationale, setRationale] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const sourceRef = useRef(sourceItemId);
  sourceRef.current = sourceItemId;

  useEffect(() => {
    if (isOpen && items.length === 0) void fetchItems();
    if (isOpen) void fetchFromSupabase();
  }, [isOpen, items.length, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    const src = sourceRef.current;
    setSelectedItemIds(src ? [src] : []);
    setCanonicalItemId(src ?? '');
    setClusterTitle('');
    setSubtype('duplicate');
    setRationale('');
    setSearchQuery('');
    setError(null);
  }, [isOpen]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (i) =>
        i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q)
    );
  }, [items, searchQuery]);

  const selectedItems = useMemo(
    () => items.filter((i) => selectedItemIds.includes(i.id)),
    [items, selectedItemIds]
  );

  const handleToggleItem = (itemId: string) => {
    setSelectedItemIds((prev) => {
      if (prev.includes(itemId)) {
        const next = prev.filter((id) => id !== itemId);
        if (canonicalItemId === itemId) setCanonicalItemId(next[0] ?? '');
        return next;
      }
      const next = [...prev, itemId];
      if (!canonicalItemId) setCanonicalItemId(itemId);
      return next;
    });
  };

  const handleSetCanonical = (itemId: string) => {
    if (selectedItemIds.includes(itemId)) setCanonicalItemId(itemId);
  };

  const handleCreate = async () => {
    setError(null);
    if (selectedItemIds.length < 2) {
      setError('Select at least 2 items to create a cluster');
      return;
    }
    if (!canonicalItemId) {
      setError('Select a canonical item');
      return;
    }
    if (!rationale.trim()) {
      setError('Provide a rationale for clustering');
      return;
    }

    setIsCreating(true);
    try {
      const group = await createGroup({
        type: 'cluster',
        subtype,
        status: 'active',
        title: clusterTitle.trim() || undefined,
        memberItemIds: selectedItemIds,
        canonicalItemId,
        rationale: rationale.trim(),
        createdBy: userId ?? 'unknown',
      });
      if (group) {
        onCreated?.(group.id);
        onClose();
      } else {
        setError('Failed to create cluster');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create cluster');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Layers className="h-6 w-6 text-[var(--text-ter)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Create Cluster</h2>
              <p className="text-sm text-[var(--text-ter)]">Group related items (canonical relationships)</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Cluster Name <span className="text-[var(--text-ter)] font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={clusterTitle}
              onChange={(e) => setClusterTitle(e.target.value)}
              placeholder="e.g. Login flow duplicates"
              className="w-full px-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-[var(--slate)]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Cluster Type</label>
            <div className="grid grid-cols-2 gap-2">
              {CLUSTER_SUBTYPES.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setSubtype(t.value)}
                  className={`p-3  border-2 text-left ${
                    subtype === t.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <div className="font-medium text-sm text-[var(--text-pri)]">{t.label}</div>
                  <div className="text-xs text-[var(--text-ter)] mt-0.5">{t.description}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
              Select Items ({selectedItemIds.length} selected)
            </label>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search items..."
                className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm"
              />
            </div>
            <div className="border border-[var(--rule)] max-h-48 overflow-y-auto">
              {filteredItems.length === 0 && !searchQuery ? (
                <div className="p-4 text-center text-[var(--text-ter)] text-sm">No items found</div>
              ) : (
                filteredItems.map((item) => {
                  const isSelected = selectedItemIds.includes(item.id);
                  const isCanonical = canonicalItemId === item.id;
                  return (
                    <div
                      key={item.id}
                      className={`flex items-center gap-3 p-3 border-b border-[var(--rule)] last:border-b-0 hover:bg-[var(--surface)] ${
                        isSelected ? 'bg-[var(--slate-dim)]' : ''
                      }`}
                    >
                      <button onClick={() => handleToggleItem(item.id)} className="flex-shrink-0">
                        {isSelected ? (
                          <CheckCircle className="h-5 w-5 text-[var(--text-ter)]" />
                        ) : (
                          <Circle className="h-5 w-5 text-[var(--text-ter)]" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-[var(--text-pri)] truncate">{item.title}</div>
                        {item.description && (
                          <div className="text-xs text-[var(--text-ter)] truncate">{item.description}</div>
                        )}
                      </div>
                      {isSelected && (
                        <button
                          onClick={() => handleSetCanonical(item.id)}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                            isCanonical ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--mustard-dim)]'
                          }`}
                          title="Set as canonical"
                        >
                          <Star className={isCanonical ? 'fill-current h-3 w-3' : 'h-3 w-3'} />
                          {isCanonical ? 'Canonical' : 'Set canonical'}
                        </button>
                      )}
                    </div>
                  );
                })
              )}
              {searchQuery.trim() && (
                <button
                  type="button"
                  onClick={() => {
                    const newId = crypto.randomUUID();
                    createItem({ id: newId, title: searchQuery.trim(), createdBy: 'unknown', tags: [] });
                    handleToggleItem(newId);
                    setSearchQuery('');
                  }}
                  className="w-full flex items-center gap-2 p-3 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] border-t border-[var(--rule)] transition-colors"
                >
                  <Plus className="h-4 w-4 flex-shrink-0" />
                  <span className="text-sm font-medium">Create item: "{searchQuery.trim()}"</span>
                </button>
              )}
            </div>
          </div>

          {selectedItems.length > 0 && (
            <div className="bg-[var(--surface)] p-4">
              <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2">Selected Items</h4>
              <div className="flex flex-wrap gap-2">
                {selectedItems.map((item) => (
                  <span
                    key={item.id}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${
                      item.id === canonicalItemId ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' : 'bg-[var(--bg-raised)] border border-[var(--rule)]'
                    }`}
                  >
                    {item.id === canonicalItemId && <Star className="h-3 w-3 fill-current" />}
                    {item.title}
                    <button onClick={() => handleToggleItem(item.id)} className="ml-1 text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Clustering Rationale <span className="text-[var(--coral)]">*</span>
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Explain why these items should be clustered together"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={selectedItemIds.length < 2 || !canonicalItemId || !rationale.trim() || isCreating}
            className="px-4 py-2 bg-[var(--slate)] text-white font-medium hover:bg-[var(--slate)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Creating...' : 'Create Cluster'}
          </button>
        </div>
      </div>
    </div>
  );
}
